import {
  createVenueTag as createVenueTagApi,
  deleteVenueTag as deleteVenueTagApi,
  getVenueTagList as getVenueTagListApi,
  updateVenueTag as updateVenueTagApi,
} from '../../../shared/api/venueTagApi';
import { VenueData, VenueTagData } from '../../../shared/dto/EntityRepository';
import {
  CreateTagReq,
  DeleteTagReq,
  UpdateTagReq,
} from '../../../shared/dto/ReqDtoRepository';
import { useApiWrapper } from '../../../shared/hooks/useApi/apiWrapper';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useTempAlertStore } from '../../../shared/store/portal/tempAlertStore';
import type { ResourceStatus } from '../../../shared/util/scopedResource';
import { useAdminVenueStore } from '../store/adminVenueStore';
import { useAdminVenueTagStore } from '../store/adminVenueTagStore';

interface UseAdminVenueTagResult {
  tags: VenueTagData[];
  status: ResourceStatus;
  getVenueTagList: () => Promise<void>;
  createVenueTag: (data: CreateTagReq) => Promise<void>;
  updateVenueTag: (data: UpdateTagReq) => Promise<void>;
  deleteVenueTag: (data: DeleteTagReq) => Promise<void>;
  clearVenueTags: () => void;
}

let latestVenueTagListRequestId = 0;
let venueTagMutationVersion = 0;

const getActiveVenueDetail = (): VenueData | null => {
  const activeVenue = useAdminVenueStore.getState().activeVenue;
  if (!activeVenue || !Array.isArray((activeVenue as VenueData).openingHours)) {
    return null;
  }
  return activeVenue as VenueData;
};

const syncActiveVenueTag = (tag: VenueTagData) => {
  const venueStore = useAdminVenueStore.getState();
  const detail = getActiveVenueDetail();
  if (detail?.tags.some((current) => current.id === tag.id)) {
    venueStore.setActiveVenue({
      ...detail,
      tags: detail.tags.map((current) => current.id === tag.id ? tag : current),
    });
  }
  if (
    venueStore.multiVenueStatus === 'success'
    && venueStore.multiSelectedVenues.some((venue) =>
      venue.tags.some((current) => current.id === tag.id)
    )
  ) {
    venueStore.setMultiSelectedVenues(venueStore.multiSelectedVenues.map((venue) => ({
      ...venue,
      tags: venue.tags.map((current) => current.id === tag.id ? tag : current),
    })));
  }
};

const removeVenueTagReferences = (tagId: number) => {
  const venueStore = useAdminVenueStore.getState();
  const detail = getActiveVenueDetail();
  if (detail?.tags.some((tag) => tag.id === tagId)) {
    venueStore.setActiveVenue({
      ...detail,
      tags: detail.tags.filter((tag) => tag.id !== tagId),
    });
  }
  if (
    venueStore.multiVenueStatus === 'success'
    && venueStore.multiSelectedVenues.some((venue) =>
      venue.tags.some((tag) => tag.id === tagId)
    )
  ) {
    venueStore.setMultiSelectedVenues(venueStore.multiSelectedVenues.map((venue) => ({
      ...venue,
      tags: venue.tags.filter((tag) => tag.id !== tagId),
    })));
  }

  if (venueStore.filterData.tagIds.includes(tagId)) {
    venueStore.setFilterData({
      ...venueStore.filterData,
      tagIds: venueStore.filterData.tagIds.filter((id) => id !== tagId),
    });
  }
};

const pruneMissingVenueTagFilters = (tags: VenueTagData[]) => {
  const venueStore = useAdminVenueStore.getState();
  const validTagIds = new Set(tags.map((tag) => tag.id));
  const tagIds = venueStore.filterData.tagIds.filter((tagId) => validTagIds.has(tagId));
  if (tagIds.length === venueStore.filterData.tagIds.length) return;
  venueStore.setFilterData({ ...venueStore.filterData, tagIds });
};

export const useAdminVenueTag = (): UseAdminVenueTagResult => {
  const store = useAdminVenueTagStore();
  const withApiHandler = useApiWrapper();
  const clearModal = useModalStore((state) => state.clearModal);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((state) => state.setDeletedTempAlert);

  const getVenueTagList = async () => {
    const requestId = ++latestVenueTagListRequestId;
    const mutationVersion = venueTagMutationVersion;
    const isCurrentRequest = () => (
      requestId === latestVenueTagListRequestId
      && mutationVersion === venueTagMutationVersion
    );
    store.beginVenueTagListFetch();
    const succeeded = await withApiHandler(async () => {
      try {
        const response = await getVenueTagListApi();
        if (!isCurrentRequest()) return false;
        store.setTags(response.data);
        pruneMissingVenueTagFilters(response.data);
        return true;
      } catch (error) {
        if (!isCurrentRequest()) return false;
        throw error;
      }
    }, ['get-venue-tag-list']);
    if (!isCurrentRequest()) return;
    if (!succeeded) {
      store.setVenueTagListError();
    }
  };

  const createVenueTag = async (data: CreateTagReq) => {
    await withApiHandler(async () => {
      const response = await createVenueTagApi(data);
      venueTagMutationVersion += 1;
      store.afterCreateTag(response.data);
      setUpdatedTempAlert();
      clearModal();
    }, ['create-venue-tag', data]);
  };

  const updateVenueTag = async (data: UpdateTagReq) => {
    await withApiHandler(async () => {
      const response = await updateVenueTagApi(data);
      venueTagMutationVersion += 1;
      store.afterUpdateTag(response.data);
      syncActiveVenueTag(response.data);
      setUpdatedTempAlert();
    }, ['update-venue-tag', data]);
  };

  const deleteVenueTag = async (data: DeleteTagReq) => {
    await withApiHandler(async () => {
      await deleteVenueTagApi(data.tagId);
      venueTagMutationVersion += 1;
      store.afterDeleteTag(data.tagId);
      removeVenueTagReferences(data.tagId);
      setDeletedTempAlert();
    }, ['delete-venue-tag', data.tagId]);
  };

  const clearVenueTags = () => {
    latestVenueTagListRequestId += 1;
    venueTagMutationVersion += 1;
    store.clearVenueTags();
  };

  return {
    tags: store.tags,
    status: store.status,
    getVenueTagList,
    createVenueTag,
    updateVenueTag,
    deleteVenueTag,
    clearVenueTags,
  };
};
